import React, { FC } from 'react';

import { mqMin, LucidTheme, bottom } from '@lucid/theme';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import Icon from '../icon/icon';
import Image from '../image/image';
import Link from '../link/link';
import Text from '../text/text';
import { ComponentImage } from '../../types/generics';

export interface QuoteCompanyProps {
  /** The name of the company. */
  name?: string | null;
  /** The logo of the company. */
  logo?: ComponentImage;
}

export interface QuoteProps {
  /** The text of the quote. */
  quote?: string | null;
  /** The author of the quote. */
  author?: {
    /** The authors first name. */
    firstName?: string | null;
    /** The authors last name. */
    lastName?: string | null;
    /** The authors title. */
    title?: string | null;
  }
  /** String for attribution below the quote */
  bottomAttribution?: string;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The author's company information. */
  company?: QuoteCompanyProps;
  /** A link to be displayed at the bottom of the quote. */
  cta?: {
    /** Checks if the Link should recieve the Button styles. */
    displayAsButton?: boolean | null;
    /** Checks if hasArrow should be displayed on the Link. */
    hasArrow?: boolean | null;
    /** The title for the Link/Button. */
    title?: string | null;
    /** The Link/Button url path. */
    url?: {
      path?: string | null;
    };
  };
}

const Quote: FC<QuoteProps> = ({
  author,
  className,
  company,
  cta,
  quote,
  bottomAttribution
}) => {
  if (!quote) return null;

  return (
    <Box
      css={(theme: LucidTheme) => ({
        padding: `${theme.space.s80}px 0`,
        [mqMin.tablet]: {
          padding: `${theme.space.s40}px 0`,
        }
      })}
      /* eslint-disable-next-line react/jsx-sort-props */
      className={className}
    >
      <Container
        as="section"
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          [mqMin.tablet]: {
            paddingRight: 0,
            paddingLeft: 0
          }
        }}
      >
        <Box pb={7}>
          <Box
            css={(theme: LucidTheme) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingBottom: theme.space.s64,
              [mqMin.tablet]: {
                alignItems: 'start',
                paddingBottom: theme.space.s24
              }
            })}
          >
            {company && company.logo && (
              <Box
                css={(theme: LucidTheme) => ({
                  paddingBottom: theme.space.s16,
                  width: '100%',
                  maxWidth: '170px',
                  height: '100%'
                })}
              >
                <Image
                  alt={company!.logo!.imageAlt!}
                  css={{
                    maxWidth: '170px',
                    // TODO: follow up to see if this is needed.
                    // '& > .image': {
                    //   filter: 'grayscale(1)'
                    // }
                  }}
                  src={company!.logo!.imageSrc!}
                />
              </Box>
            )}
            {(author && author.firstName) && (
              <Heading as="h2" variant="headlineM">
                {`${author.firstName} ${author.lastName ? author.lastName : ''}`}
              </Heading>
            )}
            {(author && author.title) && (
              <Heading as="h3" variant="headlineM">{author.title}</Heading>
            )}
          </Box>
          <Box
            css={{
              [mqMin.tablet]: {
                position: 'relative',
                maxWidth: bottomAttribution ? '900px' : '700px',
              }
            }}
          >
            <Icon
              color="primary"
              css={{
                [mqMin.tablet]: {
                  position: 'absolute',
                  top: '-16px',
                  left: '-96px'
                }
              }}
              type="StartQuote"
            />
            <Text
              css={(theme: LucidTheme) => ({
                paddingTop: theme.space.s32,
                paddingBottom: theme.space.s32,
                [mqMin.tablet]: {
                  paddingTop: 'unset',
                  paddingBottom: 'unset'
                }
              })}
              variant={bottomAttribution ? 'paragraphXL300' : 'paragraphM400'}
            >
              {quote}
            </Text>
            <Icon
              color="primary"
              css={{
                [mqMin.tablet]: {
                  position: 'absolute',
                  bottom: '32px',
                  right: '-32px'
                }
              }}
              type="EndQuote"
            />
            {bottomAttribution && (
              <Text variant="interactiveS500">
                -&nbsp;
                {bottomAttribution}
              </Text>
            )}
          </Box>
          {cta?.url?.path && (
            <Link
              css={(theme: LucidTheme) => ({
                width: '80%',
                [mqMin.tablet]: {
                  width: 'inherit',
                  paddingTop: theme.space.s40
                }
              })}
              displayAsButton={cta.displayAsButton || false}
              hasArrow={cta.hasArrow || false}
              href={cta.url.path}
              iconColor="indigo.60"
              variant="secondary"
            >
              {cta?.title}
            </Link>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export { Quote };
export default Quote;
